import React, { useEffect, useMemo, useState } from "react";
import { useGetAllJobsListQuery } from "../../features/jobs/jobsApiSlice";
import { useLocation } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import OfficeFooter from "../Office/OfficeFooter";
import FiltersSidebar from "./FiltersSidebar/FiltersSidebar";
import JobListItem from "./JobListItem";
import Map from "./Map";
import Pagination from "./Pagination";
import { useTranslation } from 'react-i18next';
import { fireCategoriesTr } from '../../utils/firebase.config';
import { selectCurrentLanguage, selectCurrentLocation, selectCurrentSocket } from "../../features/auth/authSlice";
import { useSelector } from 'react-redux';
import { getCookieConsentChoice } from '../CookieConsent/CookieConsent';
import MapConsentNotice from '../CookieConsent/MapConsentNotice';

const FILTER_STORAGE_KEY = 'help2fix.jobs.filters.v3';
const ODESA_CENTER = { lat: 46.482526, lng: 30.7233095 };

const readSavedFilters = () => {
    try {
        return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY)) || {};
    } catch (error) {
        console.warn('Saved job filters could not be read.', error);
        return {};
    }
};

class JobsErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error('Jobs page failed:', error, info);
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        return <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <h3>{this.props.title}</h3>
            <p>{this.props.message}</p>
            <button className="button" type="button" onClick={() => window.location.reload()}>{this.props.reload}</button>
        </div>;
    }
}

const JobsView = ({ isMapApiLoaded }) => {
    const { t } = useTranslation();
    const lang = useSelector(selectCurrentLanguage);
    const masterCurrentLocation = useSelector(selectCurrentLocation);
    const socket = useSelector(selectCurrentSocket);
    const location = useLocation();
    const saved = useMemo(readSavedFilters, []);
    const { categoryId, title: category, pos, masterPos, geoError } = location?.state || {};
    const initialCenter = pos || saved.center || masterPos || masterCurrentLocation || ODESA_CENTER;

    const [translations, setTranslations] = useState(null);
    const [center, setCenter] = useState(initialCenter);
    const [bounds, setBounds] = useState(null);
    const [page, setPage] = useState(1);
    const [categories, setCategories] = useState(categoryId ? [Number(categoryId)] : (saved.categories || []));
    const [payment, setPayment] = useState(saved.payment || null);
    const [currencyId, setCurrencyId] = useState(saved.currencyId || null);
    const [sort, setSort] = useState(saved.sort || 'newest');
    const [locationFilter, setLocationFilter] = useState(saved.locationFilter || null);
    const [geolocationError, setGeolocationError] = useState(geoError || null);
    const [newJobsCount, setNewJobsCount] = useState(0);
    const mapZoom = 13;

    const queryArgs = bounds
        ? { page, bounds, categories, payment, currencyId, title: category, sort }
        : skipToken;
    const { data, isLoading, isFetching, isError, refetch } = useGetAllJobsListQuery(queryArgs);
    const { jobs = [], total = 0, lastPage = 0, categoryCounts = [], paymentBounds = null } = data || {};

    useEffect(() => {
        if (!socket) return undefined;
        const onJobCreated = () => setNewJobsCount(count => count + 1);
        socket.on('jobCreated', onJobCreated);
        return () => socket.off('jobCreated', onJobCreated);
    }, [socket]);

    const showNewJobs = async () => {
        setPage(1);
        setNewJobsCount(0);
        if (bounds) await refetch();
    };

    useEffect(() => {
        fireCategoriesTr(setTranslations).catch(error => {
            console.warn('Category translations are unavailable; using source names.', error);
        });
    }, []);

    useEffect(() => {
        if (data?.page) setPage(data.page);
    }, [data?.page]);

    useEffect(() => {
        setPage(1);
    }, [bounds, categories, payment, currencyId, sort]);

    useEffect(() => {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
            center,
            categories,
            payment,
            currencyId,
            sort,
            locationFilter
        }));
    }, [center, categories, payment, currencyId, sort, locationFilter]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (pos || masterPos) return;
        if (!navigator.geolocation) {
            setGeolocationError('UNSUPPORTED');
            return;
        }

        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'geolocation' }).then(permission => {
                if (permission.state !== 'granted') {
                    setGeolocationError(permission.state === 'denied' ? 1 : 'NOT_REQUESTED');
                    return;
                }
                navigator.geolocation.getCurrentPosition(({ coords }) => {
                    setCenter({ lat: coords.latitude, lng: coords.longitude });
                    setGeolocationError(null);
                }, error => setGeolocationError(error?.code || 'UNAVAILABLE'), {
                    timeout: 10000,
                    maximumAge: 300000,
                    enableHighAccuracy: false
                });
            }).catch(() => {});
        }
    }, []);

    const translatedCategory = rawCategory => {
        const index = translations?.input?.indexOf(rawCategory);
        return translations?.translated?.[index]?.[lang] || rawCategory;
    };

    const geolocationReasonKey = geolocationError === 1
        ? 'GeolocationPermissionDenied'
        : geolocationError === 2
            ? 'GeolocationPositionUnavailable'
            : geolocationError === 3
                ? 'GeolocationTimeout'
                : geolocationError === 'UNSUPPORTED'
                    ? 'GeolocationUnsupported'
                    : null;

    return <div className="full-page-container with-map" style={{ maxHeight: '95vh' }}>
        <FiltersSidebar
            payment={payment}
            setPayment={setPayment}
            currencyId={currencyId}
            setCurrencyId={setCurrencyId}
            paymentBounds={paymentBounds}
            categories={categories}
            setCategories={setCategories}
            categoryCounts={categoryCounts}
            setCenter={setCenter}
            locationFilter={locationFilter}
            setLocationFilter={setLocationFilter}
            sort={sort}
            setSort={setSort}
            isMapApiLoaded={isMapApiLoaded} />

        <div style={{ maxHeight: '95vh' }} className="full-page-content-container">
            <div className="full-page-content-inner">
                <h3 className="page-title">{t('SearchResults')}</h3>
                {newJobsCount > 0 && <button type="button" className="button ripple-effect margin-top-15"
                    onClick={showNewJobs}>{t('NewJobsAvailable', { count: newJobsCount })}</button>}

                {isError && <div style={{ padding: '20px 0', color: '#b42318' }}>
                    <p>{t('JobsLoadError')}</p>
                    <button className="button" type="button" onClick={refetch}>{t('Retry')}</button>
                </div>}

                <div className="listings-container compact-list-layout margin-top-35 margin-bottom-25"
                    aria-busy={isLoading || isFetching}>
                    {!isLoading && !isError && jobs.map(job => <JobListItem
                        key={job.id}
                        {...job}
                        category={translatedCategory(job.category)}
                        isHomePage={false} />)}
                </div>

                <div className="clearfix"></div>
                {!isError && <Pagination total={total} setPage={setPage} currentPage={page} lastPage={lastPage} />}
                <div className="clearfix"></div>
                <OfficeFooter />
            </div>
        </div>

        <div style={{ maxHeight: '95vh' }} className="full-page-map-container">
            {geolocationError && <div style={{
                position: 'absolute', zIndex: 5, left: 16, right: 16, top: 16,
                padding: '10px 14px', background: 'rgba(255,255,255,0.96)',
                borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.14)'
            }}>
                <strong>{t('EnableGeolocationHint')}</strong>
                {geolocationReasonKey && <div style={{ fontSize: 13, marginTop: 4, color: '#b42318' }}>
                    {t(geolocationReasonKey)}
                </div>}
                <div style={{ fontSize: 13, marginTop: 4 }}>{t('GeolocationBrowserInstructions')}</div>
                <details style={{ fontSize: 13, marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer' }}>{t('HowToEnableGeolocation')}</summary>
                    <ul style={{ margin: '6px 0 0 18px' }}>
                        <li>{t('GeolocationSafariInstructions')}</li>
                        <li>{t('GeolocationMacInstructions')}</li>
                        <li>{t('GeolocationChromeInstructions')}</li>
                    </ul>
                </details>
            </div>}
            <div className="filter-button-container">
                <button className="enable-filters-button">
                    <i className="enable-filters-button-icon"></i>
                    <span className="show-text">{t('ShowFilters')}</span>
                    <span className="hide-text">{t('HideFilters')}</span>
                </button>
                <div className="filter-button-tooltip">{t('ClickToExpandSidebarWithFilters')}</div>
            </div>

            {center && !isMapApiLoaded && getCookieConsentChoice() !== 'all' && <MapConsentNotice />}
            {center && isMapApiLoaded && <Map
                lang={lang}
                trCategoriesArr={translations}
                jobs={jobs}
                mapZoom={mapZoom}
                setBounds={setBounds}
                bounds={bounds}
                center={center}
                isMapApiLoaded={isMapApiLoaded} />}
        </div>
    </div>;
};

const Jobs = props => {
    const { t } = useTranslation();
    return <JobsErrorBoundary
        title={t('SomethingWentWrong')}
        message={t('ReloadPageMessage')}
        reload={t('ReloadPage')}>
        <JobsView {...props} />
    </JobsErrorBoundary>;
};

export default Jobs;
